import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, FileText, LogOut, MessageCircle, Pencil, Phone, UserX } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useApi } from "@/lib/useApi";
import { useT, Bi } from "@/lib/i18n";
import { inr, monthLabel, dateLabel, waLink, reminderText } from "@/lib/format";
import { Card, ErrorState, ListSkeleton } from "@/components/common";
import { StatusPill } from "@/components/StatusPill";
import { MarkPaidSheet, downloadReceipt } from "@/components/MarkPaidSheet";
import { MoveOutSheet } from "@/components/MoveOutSheet";
import { ShareLinkCard, IdCard } from "@/components/TenantCards";
import { BottomSheet } from "@/components/BottomSheet";
import { Field, BigInput, MoneyInput } from "@/components/form";

function EditSheet({ tenant, open, onClose, onSaved }: any) {
  const { t } = useT();
  const [f, setF] = useState({ name: tenant.name, phone: tenant.phone, monthly_rent: tenant.next_rent?.amount ?? tenant.monthly_rent, expected_move_out: tenant.expected_move_out || "" });
  const save = async () => {
    try {
      const body: any = { name: f.name, phone: f.phone, monthly_rent: f.monthly_rent };
      if (f.expected_move_out) body.expected_move_out = f.expected_move_out; else body.clear_expected_move_out = true;
      await api.patch(`/tenants/${tenant.id}`, body);
      toast.success(t("saved"));
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <BottomSheet open={open} onOpenChange={(o) => !o && onClose()} title={<Bi k="edit_tenant" />} testId="edit-tenant-sheet">
      <div className="space-y-4">
        <Field label={t("tenant_name")}><BigInput data-testid="edit-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label={t("phone_label")}><BigInput data-testid="edit-phone-input" inputMode="numeric" maxLength={10} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value.replace(/\D/g, "") })} /></Field>
        <Field label={t("monthly_rent")} hint={t("rent_change_note")}><MoneyInput data-testid="edit-rent-input" value={f.monthly_rent} onChange={(v) => setF({ ...f, monthly_rent: v })} /></Field>
        <Field label={`${t("expected_move_out")} (${t("optional")})`}><BigInput data-testid="edit-moveout-input" type="date" value={f.expected_move_out} onChange={(e) => setF({ ...f, expected_move_out: e.target.value })} /></Field>
        <Button data-testid="edit-save-btn" onClick={save} className="w-full h-14 rounded-xl text-base bg-emerald-700 hover:bg-emerald-800">{t("save")}</Button>
      </div>
    </BottomSheet>
  );
}

function Header({ d, onEdit }: { d: any; onEdit: () => void }) {
  const { t, lang } = useT();
  const nav = useNavigate();
  return (
    <div className="mb-4 pg-rise">
      <button data-testid="tenant-back-btn" onClick={() => nav(-1)} className="h-10 flex items-center gap-1 text-sm font-semibold text-slate-600"><ArrowLeft className="h-4 w-4" />{t("back")}</button>
      <div className="flex items-start justify-between gap-3 mt-1">
        <div>
          <h1 data-testid="tenant-detail-name" className="text-2xl font-bold tracking-tight">{d.name}</h1>
          <p className="text-sm text-slate-500">{t("room")} {d.room_name} · {t("bed")} {d.bed_label} · {t("joined")} {dateLabel(d.move_in_date, lang)}</p>
          <p className="text-sm mt-1"><b className="font-mono-num">{inr(d.monthly_rent)}</b>/mo · {t("deposit")} {inr(d.deposit)}</p>
          {d.next_rent && <p data-testid="tenant-next-rent" className="text-xs text-indigo-700 mt-1">{t("next_rent")} {monthLabel(d.next_rent.effective_month, lang)}: {inr(d.next_rent.amount)}</p>}
          {d.status === "moved_out" && <p className="text-xs font-semibold text-rose-600 mt-1">{t("moved_out")} · {dateLabel(d.move_out_date, lang)}</p>}
        </div>
        {d.status === "active" && <Button data-testid="tenant-edit-btn" variant="outline" className="h-11 w-11 p-0 rounded-full" onClick={onEdit}><Pencil className="h-4 w-4" /></Button>}
      </div>
    </div>
  );
}

function Ledger({ d, pg, onPay }: { d: any; pg: string; onPay: (r: any) => void }) {
  const { t, lang } = useT();
  return (
    <Card className="p-4" data-testid="tenant-ledger">
      <p className="font-bold mb-2"><Bi k="rent_history" /></p>
      <ul className="divide-y divide-slate-100">
        {d.records.map((r) => (
          <li key={r.id} className="py-3 flex items-center gap-2" data-testid={`ledger-row-${r.month}`}>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">{monthLabel(r.month, lang, true)}</p>
              <p className="text-xs text-slate-500 font-mono-num">{inr(r.amount_due - r.outstanding)} / {inr(r.amount_due)}
                {r.is_prorated && <span className="ml-1 text-indigo-600">· {t("prorated")} {r.days_billed}/{r.days_in_month} {t("days")}</span>}
                {r.deposit_adjustment > 0 && <span className="ml-1 text-indigo-600">· {t("deposit_adjustment")} {inr(r.deposit_adjustment)}</span>}
              </p>
            </div>
            <StatusPill status={r.status} testId={`ledger-status-${r.month}`} />
            {r.outstanding > 0 && d.status === "active" && (
              <div className="flex gap-1">
                <a data-testid={`ledger-remind-${r.month}`} href={waLink(d.phone, reminderText(d.name, r.outstanding, r.month, pg))} target="_blank" rel="noreferrer" className="h-10 w-10 rounded-full bg-[#25D366]/10 text-[#128C4A] grid place-items-center"><MessageCircle className="h-4 w-4" /></a>
                <Button data-testid={`ledger-pay-${r.month}`} onClick={() => onPay({ ...r, tenant_name: d.name })} className="h-10 rounded-full bg-emerald-700 hover:bg-emerald-800 px-3 text-xs">{t("mark_paid")}</Button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Payments({ d }: { d: any }) {
  const { t, lang } = useT();
  return (
    <Card className="p-4" data-testid="tenant-payments">
      <p className="font-bold mb-2"><Bi k="payments" /></p>
      {d.payments.length === 0 ? <p className="text-sm text-slate-500">{t("no_payments")}</p> : (
        <ul className="divide-y divide-slate-100">
          {d.payments.map((p) => (
            <li key={p.id} className="py-3 flex items-center justify-between gap-2">
              <div>
                <p className="text-sm font-semibold font-mono-num">{inr(p.amount)} · {t(p.mode)}</p>
                <p className="text-xs text-slate-500">{p.receipt_no} · {dateLabel(p.paid_on, lang)}</p>
              </div>
              <Button data-testid={`receipt-download-${p.receipt_no}`} variant="outline" className="h-10 rounded-full" onClick={() => downloadReceipt(p)}><FileText className="h-4 w-4 mr-1" />{t("receipt")}</Button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function MovedOutCard({ d, onErased }: { d: any; onErased: () => void }) {
  const { t } = useT();
  const s = d.settlement;
  const erase = async () => {
    if (!window.confirm(t("erase_help"))) return;
    try { await api.delete(`/tenants/${d.id}/personal-data`); toast.success(t("erased")); onErased(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card className="p-4 space-y-2" data-testid="tenant-settlement-card">
      <p className="font-bold"><Bi k="settlement" /></p>
      {s && <p className="text-sm">{t("refund")}: <b className="font-mono-num text-emerald-700">{inr(s.refund_to_tenant)}</b> · {t("deposit_applied")}: <b className="font-mono-num">{inr(s.deposit_applied_to_rent)}</b></p>}
      {!d.erased_at ? (
        <Button data-testid="tenant-erase-btn" variant="outline" className="w-full h-12 rounded-xl text-rose-600 border-rose-200" onClick={erase}><UserX className="h-4 w-4 mr-1" />{t("erase_data")}</Button>
      ) : <p className="text-xs text-slate-500">{t("erased")}</p>}
      <p className="text-xs text-slate-500">{t("erase_help")}</p>
    </Card>
  );
}

export default function TenantDetail() {
  const { id } = useParams();
  const { t } = useT();
  const { property, reloadProperties } = useAuth();
  const { data: d, loading, error, reload } = useApi<any>(`/tenants/${id}`);
  const [pay, setPay] = useState<any>(null);
  const [edit, setEdit] = useState(false);
  const [moveOut, setMoveOut] = useState(false);
  if (loading && !d) return <ListSkeleton rows={5} />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const pg = property?.name || "";
  return (
    <div>
      <Header d={d} onEdit={() => setEdit(true)} />
      {d.status === "active" && d.phone && (
        <div className="grid grid-cols-2 gap-2 mb-4">
          <a data-testid="tenant-call-btn" href={`tel:+91${d.phone}`} className="h-12 rounded-xl border border-slate-200 bg-white font-semibold flex items-center justify-center gap-1.5 pg-tap"><Phone className="h-4 w-4" />+91 {d.phone}</a>
          <Button data-testid="tenant-moveout-btn" variant="outline" className="h-12 rounded-xl text-rose-600 border-rose-200" onClick={() => setMoveOut(true)}><LogOut className="h-4 w-4 mr-1" />{t("move_out")}</Button>
        </div>
      )}
      <div className="space-y-3">
        <Ledger d={d} pg={pg} onPay={setPay} />
        <Payments d={d} />
        {d.status === "active" ? <ShareLinkCard tenant={d} /> : <MovedOutCard d={d} onErased={reload} />}
        <IdCard tenant={d} onChanged={reload} />
      </div>
      <MarkPaidSheet record={pay} onClose={() => setPay(null)} onSaved={reload} />
      {edit && <EditSheet tenant={d} open={edit} onClose={() => setEdit(false)} onSaved={reload} />}
      {d.status === "active" && <MoveOutSheet tenant={d} open={moveOut} onClose={() => setMoveOut(false)} onDone={() => { reload(); reloadProperties(); }} />}
    </div>
  );
}
